'use client'

import { generateTagTrackingValue } from '@/components/common/product-card/helper'
import { ProductAddMobile } from '@/components/common/product-card/product-add-mobile'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { stringToSlug } from '@/lib/utils/common'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useProductCardContext } from './context/product-card-context'
import { ProductItemHeader } from './product-item-header'
import { ProductItemImages } from './product-item-images'
import { ProductItemSizes } from './product-item-sizes'

export const ProductItemThumbnail = ({
  gaTrackingValue,
  gaTrackingLabel,
  disableNavigation,
  hideSizes,
}: {
  gaTrackingValue?: string
  gaTrackingLabel?: string
  disableNavigation?: boolean
  hideSizes?: boolean
}) => {
  const { product, trackingClickProduct, selectedColor } =
    useProductCardContext()
  const t = useTranslations('product_detail.title')

  // Auto-generate tracking value from product tags if not provided
  const finalTrackingValue = product?.is_setdo
    ? 'product__listing_set-do'
    : gaTrackingValue || generateTagTrackingValue(product.tags)

  const handleClick = () => {
    if (!disableNavigation) {
      trackingClickProduct()
    }
  }

  const isFifaProduct = product?.collections?.includes(
    '69b22f304a3f281d3a477e93'
  )

  const isHelloKittyProduct = product?.collections?.includes(
    '6ab1e99a600ab031c45d18b9'
  )

  const isHoangSaoProduct = product?.collections?.includes(
    '6ad03065095911037a562ed2'
  )

  const isPokemonProduct = product?.collections?.includes(
    '6ad98fd786495a6f4a618fe7'
  )

  const hasIconThumbnail =
    !product?.icon_thumbnail?.hide && !!product?.icon_thumbnail?.image

  const overlayLabel = product?.coming_soon
    ? t('coming_soon')
    : product?.is_out_of_stock
      ? t('out_of_stock')
      : null

  const content = (
    <>
      <ProductItemImages />

      {overlayLabel ? (
        <div className='absolute inset-0 z-[2] flex items-center justify-center rounded-lg bg-black/40'>
          <span className='font-criteria text-base font-semibold uppercase text-light md:text-lg'>
            {overlayLabel}
          </span>
        </div>
      ) : null}

      {isFifaProduct ? (
        <div className='absolute left-2 top-2 z-[2] w-4 md:w-6'>
          <Image
            src={ImageHelper.getN7ImageUrl(
              '/image/2026/03/19/logo_fifa_official_1.png'
            )}
            alt='fifa-logo'
            width={80}
            height={120}
            className='h-auto w-full object-contain'
            loading='lazy'
          />
        </div>
      ) : null}

      {isHelloKittyProduct ? (
        <div className='absolute left-[5%] top-[4.4%] z-[2] w-[20.5%]'>
          <Image
            src={ImageHelper.getImageUrl(
              '/image/2026/10/16/hello-kitty-collab-logo.png'
            )}
            alt='hello-kitty-logo'
            width={140}
            height={16}
            className='h-auto w-full object-contain'
            loading='lazy'
          />
        </div>
      ) : null}

      {isHoangSaoProduct ? (
        <div className='absolute left-3 top-3 z-[2] w-[30%] md:w-1/5'>
          <Image
            src={ImageHelper.getImageUrl(
              '/image/2026/10/20/coolmate-x-hoang-sao-logo.png'
            )}
            alt='coolmate-x-hoang-sao-logo'
            width={600}
            height={169}
            className='h-auto w-full object-contain'
            loading='lazy'
          />
        </div>
      ) : null}

      {isPokemonProduct ? (
        <div className='absolute left-[5%] top-[4.4%] z-[2] w-[22%]'>
          <Image
            src={ImageHelper.getImageUrl('/image/2026/11/05/pokemon.png')}
            alt='pokemon-logo'
            width={263}
            height={97}
            className='h-auto w-full object-contain'
            loading='lazy'
          />
        </div>
      ) : null}

      {(isHelloKittyProduct || isPokemonProduct || hasIconThumbnail) && (
        <div className='absolute bottom-0 left-0 z-[1] flex h-auto w-full flex-col rounded-b-lg'>
          {isPokemonProduct ? (
            <Image
              src={ImageHelper.getImageUrl(
                '/image/2026/11/05/legal_pokemon.png'
              )}
              alt='pokemon-license-legal'
              width={614}
              height={88}
              className='mb-[3.7%] ml-[3.5%] block h-auto w-[40%] object-contain'
              loading='lazy'
            />
          ) : null}
          {isHelloKittyProduct ? (
            <Image
              src={ImageHelper.getImageUrl(
                '/image/2026/10/16/hello-kitty-sanrio-legal-stacked.png'
              )}
              alt='sanrio-license-legal'
              width={224}
              height={82}
              className='mb-[3.7%] ml-[3.5%] block h-auto w-[16.4%] object-contain'
              loading='lazy'
            />
          ) : null}
          {hasIconThumbnail && (
            <Image
              src={ImageHelper.getImageUrl(
                product?.icon_thumbnail?.image as string,
                '585_663'
              )}
              width={433}
              height={62}
              alt={`${product.title} thumbnail image`}
              className='block h-auto w-full rounded-b-lg object-cover'
              loading='lazy'
            />
          )}
        </div>
      )}
      <ProductItemHeader />
    </>
  )

  return (
    <div className='group relative'>
      {disableNavigation ? (
        <div className='block h-full w-full'>{content}</div>
      ) : (
        <Link
          href={
            selectedColor
              ? `${product.href}?color=${stringToSlug(selectedColor)}`
              : product.href
          }
          className='block h-full w-full'
          {...(finalTrackingValue
            ? {
                'ga-tracking-value': finalTrackingValue,
                ...(gaTrackingLabel && {
                  'ga-tracking-label': gaTrackingLabel,
                }),
              }
            : {})}
          onClick={handleClick}
          aria-label={`View details for ${product.title}`}
        >
          {content}
        </Link>
      )}
      {!hideSizes && !overlayLabel && (
        <div className='max-lg:hidden'>
          <ProductItemSizes />
        </div>
      )}
      {!overlayLabel && (
        <ProductAddMobile
          className={hasIconThumbnail ? 'bottom-12' : 'bottom-6'}
        />
      )}
    </div>
  )
}
